import { useMemo } from 'react'
import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client'
import {
  GetRecommendedUpNextQuery,
  GetRecommendedUpNextQueryVariables,
  GetSuggestedShowsAndFilmsQuery,
  GetSuggestedShowsAndFilmsQueryVariables,
} from '@/types/codegen-federation'
import { isDefined } from '@/utils/primitives/generics'
import { getWebClient } from '../ApolloClient'
import { GET_RECOMMENDED_UP_NEXT, GET_SUGGESTED_SHOWS_AND_FILMS } from './queries'
import { RecommendedUpNextResult, SuggestedShowsAndFilmsContent, SuggestedShowsAndFilmsResult } from './types'

export const useGetRecommendedUpNext = (titleId: string, canShowEpisodeOverlay: boolean): RecommendedUpNextResult => {
  const client = getWebClient()
  const { data, loading } = useQuery<GetRecommendedUpNextQuery, GetRecommendedUpNextQueryVariables>(
    GET_RECOMMENDED_UP_NEXT,
    {
      client,
      variables: { titleId },
      skip: !canShowEpisodeOverlay,
      errorPolicy: 'all',
      context: {
        requireAuth: true,
      },
    },
  )

  return useMemo(() => {
    const recommendedUpNext = data?.recommendedUpNextV3?.content?.filter(isDefined) ?? []

    return { recommendedUpNext, loading }
  }, [data, loading])
}

export const useGetSuggestedShowsAndFilms = (titleId: string): SuggestedShowsAndFilmsResult => {
  const client = getWebClient()
  const { data, loading } = useQuery<GetSuggestedShowsAndFilmsQuery, GetSuggestedShowsAndFilmsQueryVariables>(
    GET_SUGGESTED_SHOWS_AND_FILMS,
    {
      client,
      variables: { titleId },
      skip: !titleId,
      errorPolicy: 'all',
    },
  )

  return useMemo(() => {
    const suggestedShowsAndFilms = data?.relatedTitlesV2?.content?.filter(isDefined) ?? []

    return { suggestedShowsAndFilms, loading }
  }, [data, loading])
}

export const getSuggestedShowsAndFilms = async (
  titleId: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<SuggestedShowsAndFilmsContent> => {
  if (!titleId) return []

  try {
    const { data } = await client.query<GetSuggestedShowsAndFilmsQuery, GetSuggestedShowsAndFilmsQueryVariables>({
      query: GET_SUGGESTED_SHOWS_AND_FILMS,
      variables: { titleId },
      errorPolicy: 'all',
    })

    return data?.relatedTitlesV2?.content?.filter(isDefined) ?? []
  } catch (error) {
    return []
  }
}
